import type { ReadonlyDeep } from 'type-fest';
import type { Status as SmallTrafficLightDiagramStatus } from '@/components/diagrams/SmallTrafficLightDiagram.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';
import type { CategoryJobs } from '@/helpers/trackingJob/groupTrackingJob';

import _uniq from 'lodash/uniq';

import { getBaseName } from '@/helpers/bases';
import * as jobStatuses from '@/constants/jobStatuses';

export function getJobAttributes(
  job: ReadonlyDeep<Pick<openapi.JobDetailedOpenapi, 'stops'>>,
): string[] {
  return _uniq(job.stops.flatMap((stop) => stop.attributes));
}

export function getJobIdAndJobDate(
  job: ReadonlyDeep<
    Pick<openapi.JobDetailedOpenapi, 'rockhopper_id' | 'rockhopper_date'>
  >,
): {
  jobId: number;
  jobDate: string;
} {
  return {
    jobId: job.rockhopper_id,
    jobDate: job.rockhopper_date,
  };
}

export function checkLockedOrAllocated(
  dispatchJob: ReadonlyDeep<
    Pick<
      openapi.DespatchJobsContentOpenapi[0],
      'is_locked' | 'allocation_state'
    >
  >,
): boolean {
  return dispatchJob.is_locked || dispatchJob.allocation_state === 'allocated';
}

export function checkSplitToBaseWithoutSecondDriver(
  job: ReadonlyDeep<Pick<openapi.JobDetailedOpenapi, 'split_base' | 'stops'>>,
): boolean {
  const driver2 = getStopDriver(job, 2); // TODO why exactly the second one here?
  return !!(job.split_base && !driver2);
}

export function getMainStopNum(
  job: ReadonlyDeep<
    Partial<Pick<openapi.DashboardJobsTrackingContentOpenapi[0], 'main_stop'>>
  >,
): 1 | 2 {
  if (job.main_stop === 1) return 1;
  if (job.main_stop === 2) return 2;
  return 2; // main_stop is 2 by default
}

/**
 * Sometimes drivers could be different on different stops (ex. Split-to-Base jobs)
 */
export function getMainDriver(
  job: ReadonlyDeep<
    Pick<openapi.DashboardJobsTrackingContentOpenapi[0], 'main_stop' | 'stops'>
  >,
): number {
  return getStopDriver(job, getMainStopNum(job));
}

export function getMainSuburbState(
  job: ReadonlyDeep<
    Pick<openapi.DashboardJobsTrackingContentOpenapi[0], 'main_stop' | 'stops'>
  >,
): string {
  const mainStopNum = getMainStopNum(job);
  return job.stops[mainStopNum - 1].state;
}

export function getMainStopName(
  job: ReadonlyDeep<
    Pick<openapi.DashboardJobsTrackingContentOpenapi[0], 'main_stop' | 'stops'>
  >,
): string {
  return getStopName(job, getMainStopNum(job));
}

export function getMainSuburbName(
  job: ReadonlyDeep<
    Pick<openapi.DashboardJobsTrackingContentOpenapi[0], 'main_stop' | 'stops'>
  >,
): string {
  const mainStopNum = getMainStopNum(job);
  return job.stops[mainStopNum - 1].suburb;
}

export function getStopDriver(
  job: ReadonlyDeep<
    Pick<openapi.DashboardJobsTrackingContentOpenapi[0], 'stops'>
  >,
  stopNum: 1 | 2 | 3,
): number {
  const _stopDriver = job.stops[stopNum - 1].driver_is;
  // TODO
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  return _stopDriver ?? NaN;
  // return parseInt(job[`driver${stopNum}`] ?? ''); // if driverVal is 'NOT ALLOCATED' or 'None' then it returns NaN
}

export function getStopName(
  job: ReadonlyDeep<
    Pick<openapi.DashboardJobsTrackingContentOpenapi[0], 'stops'>
  >,
  stopNum: 1 | 2,
): string {
  return job.stops[stopNum - 1].name;
}

export function getStopRequired(
  job: ReadonlyDeep<
    Pick<openapi.DashboardJobsTrackingContentOpenapi[0], 'stops'>
  >,
  stopNum: 1 | 2,
): string | null {
  return job.stops[stopNum - 1].required_at;
}

export function getStopArrived(
  job: ReadonlyDeep<
    Pick<openapi.DashboardJobsTrackingContentOpenapi[0], 'stops'>
  >,
  stopNum: 1 | 2 | 3,
): string | null {
  return job.stops[stopNum - 1].arrived_at;
}

export function getStopDeparted(
  job: ReadonlyDeep<
    Pick<openapi.DashboardJobsTrackingContentOpenapi[0], 'stops'>
  >,
  stopNum: 1 | 2 | 3,
): string | null {
  return job.stops[stopNum - 1].departed_at;
}

export function getStopPodTime(
  job: ReadonlyDeep<
    Pick<openapi.DashboardJobsTrackingContentOpenapi[0], 'stops'>
  >,
  stopNum: 1 | 2,
): string | null {
  return job.stops[stopNum - 1].pod_time;
}

export function getFirstTwoStopNames(
  job: ReadonlyDeep<
    Pick<openapi.DashboardJobsTrackingContentOpenapi[0], 'stops'>
  >,
): string[] {
  return [getStopName(job, 1), getStopName(job, 2)];
}

export function checkMainStop(
  job: ReadonlyDeep<
    Pick<openapi.DashboardJobsTrackingContentOpenapi[0], 'main_stop'>
  >,
  stopNum: 1 | 2 | 3,
): boolean {
  return getMainStopNum(job) === stopNum;
}

export function checkStopMainArrived(
  job: ReadonlyDeep<
    Pick<openapi.DashboardJobsTrackingContentOpenapi[0], 'main_stop' | 'stops'>
  >,
  stopNum: 1 | 2 | 3,
): boolean {
  return !!getStopArrived(job, stopNum) && checkMainStop(job, stopNum);
}

export function getStopActionDenial(
  job: ReadonlyDeep<openapi.DashboardJobsTrackingContentOpenapi[0]>,
  stopNum: 1 | 2 | 3,
): string {
  if (checkSplitToBaseWithoutSecondDriver(job)) {
    return 'checkSplitToBaseWithoutSecondDriver';
  }
  if (job.is_futile) return 'job.is_futile';
  if (!getStopDriver(job, stopNum)) return '!getStopDriver';
  if (job.cancelled_at) return 'job.cancelled_at';
  if (job.completed_at) return 'job.completed_at';
  if (getStopDeparted(job, stopNum)) return 'getStopDeparted';
  return '';
}

export function getStatus(
  job: ReadonlyDeep<openapi.DashboardJobsTrackingContentOpenapi[0]>,
  categoryJobs: CategoryJobs,
  bases: openapi.GetCatalogBases_200,
): string {
  if (job.cancelled_at) return jobStatuses.CANCELED;
  if (job.completed_at) return jobStatuses.COMPLETED;
  if (job.is_futile) return jobStatuses.FUTILE;
  if (!job.stops[0].required_at && !job.stops[1].required_at) {
    return jobStatuses.UNKNOWN;
  }
  // STOP 2
  if (job.stops[1].departed_at) return jobStatuses.STOP2_DEPARTED;
  if (job.stops[1].arrived_at) return jobStatuses.STOP2_ARRIVED;
  // SPLIT
  const driver1 = getStopDriver(job, 1);
  const driver2 = getStopDriver(job, 2);
  if (job.split_base && driver2) {
    if (
      categoryJobs.completed.includes(
        job as openapi.DashboardJobsTrackingContentOpenapi[0],
      )
    ) {
      return jobStatuses.COMPLETED;
    }
    return `${jobStatuses.STOP2_SPLIT}: ${getBaseName(job.split_base, bases)}`;
  }
  if (job.split_base && !driver2) {
    return `${jobStatuses.STOP2_SPLIT_WARN}: ${getBaseName(job.split_base, bases)}`;
  }
  // STOP 1
  if (job.stops[0].departed_at) return jobStatuses.STOP1_DEPARTED;
  if (job.stops[0].arrived_at) return jobStatuses.STOP1_ARRIVED;
  if (job.allocated_at) return jobStatuses.ALLOCATED;
  // JOB_PRE_ALLOCATED
  if (driver1 && !driver2) return jobStatuses.PRE_ALLOCATED;
  if (!driver1 && !driver2) return jobStatuses.NOT_ALLOCATED;
  // DELAY STOP1
  if (driver1 && job.stops[0].required_at && !job.stops[0].arrived_at) {
    if (job.delay_status === 1) return jobStatuses.STOP1_DELAY;
    if (job.delay_status === 2) return jobStatuses.STOP1_PRE_DELAY;
  }
  // DELAY STOP2
  if (job.stops[1].required_at && job.stops[0].departed_at) {
    if (job.delay_status === 1) return jobStatuses.STOP2_DELAY;
    if (job.delay_status === 2) return jobStatuses.STOP2_PRE_DELAY;
  }
  return jobStatuses.UNKNOWN;
}

export function getTrafficlightColors(
  job: ReadonlyDeep<openapi.DashboardJobsTrackingContentOpenapi[0]>,
): SmallTrafficLightDiagramStatus['color'][] {
  if (job.cancelled_at) return ['red', 'red', 'red'];
  if (job.completed_at) return ['green', 'green', 'green'];
  if (!job.stops[0].required_at && !job.stops[1].required_at) {
    return ['gray', 'gray', 'gray'];
  }
  // STOP 2
  if (job.stops[1].departed_at) return ['green', 'green', 'gray'];
  if (job.stops[1].arrived_at) return ['green', 'orange', 'gray'];
  // SPLIT
  const driver1 = getStopDriver(job, 1);
  const driver2 = getStopDriver(job, 2);
  if (job.split_base && driver2) return ['green', 'violet', 'green'];
  if (job.split_base && !driver2) return ['green', 'violet', 'orange'];
  // STOP 1
  if (job.stops[0].departed_at) return ['green', 'gray', 'gray'];
  if (job.stops[0].arrived_at) return ['orange', 'gray', 'gray'];
  if (job.allocated_at) return ['gray', 'gray', 'gray'];
  // JOB_PRE_ALLOCATED
  if (driver1 && !driver2) return ['gray', 'gray', 'gray'];
  if (!driver1 && !driver2) return ['gray', 'gray', 'gray'];
  // DELAY STOP1
  if (driver1 && job.stops[0].required_at && !job.stops[0].arrived_at) {
    if (job.delay_status === 1) return ['red', 'gray', 'gray'];
    if (job.delay_status === 2) return ['violet', 'gray', 'gray'];
  }
  // DELAY STOP2
  if (job.stops[1].required_at && job.stops[0].departed_at) {
    if (job.delay_status === 1) return ['green', 'red', 'gray'];
    if (job.delay_status === 2) return ['green', 'violet', 'gray'];
  }
  return ['gray', 'gray', 'gray'];
}
